import React, { useEffect, useMemo, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import HelpPanel from '@cloudscape-design/components/help-panel';
import { useAuth } from '../auth/AuthContext';
import AppShell, { AppTab } from '../components/AppShell';
import { getAnalyticsService } from '../services';
import { areasFor } from '../services/types';
import type { AppArea } from '../services/types';
import IntegrationGuide from './IntegrationGuide';
import AsnScreen from './AsnScreen';
import PromisedDateScreen from './PromisedDateScreen';
import LeadTimeScreen from './LeadTimeScreen';
import SupplierUsersScreen from './SupplierUsersScreen';
import SupplierControlScreen from './SupplierControlScreen';
import SupplierInsightsScreen from './SupplierInsightsScreen';

const AREA_VALUES: AppArea[] = ['connect', 'control', 'insights'];

/** The area named in the URL hash (e.g. "#control"), if valid; else undefined. */
function areaFromHash(): AppArea | undefined {
    const h = window.location.hash.replace(/^#/, '');
    return (AREA_VALUES as string[]).includes(h) ? (h as AppArea) : undefined;
}

export default function LandingScreen() {
    const { user, signOut } = useAuth();
    const areas = useMemo(
        () => areasFor(user?.roles, user?.isEmployee),
        [user?.roles, user?.isEmployee],
    );

    // Hooks must run unconditionally and in a stable order, so declare them
    // before any early return. `areas` may be empty (denied employee); guard the
    // initial-area access so we don't read areas[0] of an empty list.
    // Initialize from the URL hash so a reload restores the open area (e.g.
    // "#control"); fall back to the first area the user can access.
    const [area, setArea] = useState<AppArea | undefined>(() => areaFromHash() ?? areas[0]);
    const [tab, setTab] = useState<AppTab>('asn');
    const [guideOpen, setGuideOpen] = useState(false);
    // Master-only "Supplier Users" setup view, opened from the top-right action
    // beside the Integration guide (not a data tab).
    const [showUsers, setShowUsers] = useState(false);

    // Keep the URL hash in sync with the resolved active area so a reload
    // restores it. Runs unconditionally (hook order) and only when the user has
    // areas; the denied-employee case (no areas) leaves the hash untouched.
    const resolvedArea = area && areas.includes(area) ? area : areas[0];
    useEffect(() => {
        if (!resolvedArea) return;
        const next = `#${resolvedArea}`;
        if (window.location.hash !== next) {
            window.history.replaceState(null, '', next);
        }
    }, [resolvedArea]);

    // A federated employee in none of the role groups resolves to no areas.
    // Show an access-denied screen instead of falling through to Supplier
    // Gateway (which is the native-supplier default).
    if (areas.length === 0) {
        return (
            <AppShell userEmail={user?.email} onSignOut={signOut}>
                <Box textAlign="center" padding="xxl">
                    <SpaceBetween size="s">
                        <Box variant="h2">Access denied</Box>
                        <Box color="text-body-secondary">
                            Your account isn&apos;t assigned to any Supplier Gateway group. Please
                            reach out to an administrator to request access.
                        </Box>
                    </SpaceBetween>
                </Box>
            </AppShell>
        );
    }

    const activeArea = resolvedArea;

    // "Home" = the user's first/default area with the default Connect tab, and
    // the integration guide closed. Clicking the brand/title returns here.
    const goHome = () => {
        setArea(areas[0]);
        setTab('asn');
        setGuideOpen(false);
        setShowUsers(false);
    };

    // The integration guide as a right slide-over panel (Connect area only).
    const guideTools = (
        <HelpPanel header={<h2>Integration guide</h2>}>
            <IntegrationGuide />
        </HelpPanel>
    );

    // Connect area: tab row + integration guide. Area switching via the left nav.
    if (activeArea === 'connect') {
        const toggleGuide = () => {
            const next = !guideOpen;
            // Only count opens, not closes. Side effect lives here (not inside a
            // state updater) so it fires once and the updater stays pure.
            if (next) getAnalyticsService().track('integration_guide_opened');
            setGuideOpen(next);
        };
        // "Supplier Users" (self-service user management) is a master-only
        // SETUP action, not a submission-data view — so it lives beside the
        // Integration guide at the end, NOT in the ASN/Promised/Lead-time tab
        // row. A master opens it when they need to add/manage users.
        const isMaster = user?.supplierRole === 'MASTER';
        // Only the data tabs appear in the tab row now (members moved out).
        const visibleTabs: AppTab[] = ['asn', 'promised', 'leadtime'];
        const activeTab: AppTab = tab === 'members' ? 'asn' : tab;

        const navActions = (
            <SpaceBetween size="xs" direction="horizontal">
                <Button variant={guideOpen ? 'primary' : 'normal'} onClick={toggleGuide}>
                    Integration guide
                </Button>
                {isMaster ? (
                    <Button
                        variant={showUsers ? 'primary' : 'normal'}
                        onClick={() => setShowUsers((v) => !v)}
                    >
                        Supplier Users
                    </Button>
                ) : null}
            </SpaceBetween>
        );
        return (
            <AppShell
                userEmail={user?.email}
                onSignOut={signOut}
                navActions={navActions}
                // Hide the data-tab row while the master is managing users, so
                // Supplier Users reads as a separate setup view.
                tab={showUsers ? undefined : activeTab}
                onTabChange={(t) => {
                    // Picking a data tab leaves the Supplier Users setup view.
                    setShowUsers(false);
                    setTab(t);
                }}
                visibleTabs={visibleTabs}
                areas={areas}
                activeArea={activeArea}
                onAreaChange={setArea}
                onHome={goHome}
                tools={guideTools}
                toolsOpen={guideOpen}
                onToolsChange={setGuideOpen}
            >
                {showUsers && isMaster ? (
                    <SupplierUsersScreen />
                ) : (
                    <>
                        {activeTab === 'asn' ? <AsnScreen /> : null}
                        {activeTab === 'promised' ? <PromisedDateScreen /> : null}
                        {activeTab === 'leadtime' ? <LeadTimeScreen /> : null}
                    </>
                )}
            </AppShell>
        );
    }

    // Internal areas: no tab row; area switching via the left nav.
    return (
        <AppShell
            userEmail={user?.email}
            onSignOut={signOut}
            areas={areas}
            activeArea={activeArea}
            onAreaChange={setArea}
            onHome={goHome}
        >
            {activeArea === 'control' ? <SupplierControlScreen /> : <SupplierInsightsScreen />}
        </AppShell>
    );
}
